import React, { useRef } from 'react'

import { Box, Stack, TextField } from '@mui/material'

export type OtpInputProps = {
  /** Current code. Shorter than `length` is fine — the remaining boxes render empty. */
  value: string
  onChange: (next: string) => void
  /** Number of digit boxes. */
  length?: number
  /** Renders the boxes in the error style (red border). */
  error?: boolean
  disabled?: boolean
  /** Fired when the last box is filled, so a caller can auto-submit. */
  onComplete?: (code: string) => void
}

/**
 * A segmented one-time-code field — one box per digit, with auto-advance,
 * backspace-to-previous, and paste-the-whole-code support.
 *
 * Extracted on its second hand-roll (working-conventions §36). The sign-in
 * screen (`pages/SignIn/OtpVerify.tsx`) built this inline first; the phone
 * verification drawer (VERA-291) needed the same thing, and a single wide text
 * input there looked nothing like the code entry users already know from
 * signing in. `OtpVerify` should migrate onto this component next time it is
 * touched, so the two can't drift.
 *
 * Box metrics (32×44, 4px gutters, wider gap after the second box) match the
 * sign-in screen so the two read as the same control.
 */
export const OtpInput = ({
  value,
  onChange,
  length = 4,
  error,
  disabled,
  onComplete,
}: OtpInputProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const digits = Array.from({ length }, (_, i) => value[i] ?? '')

  const commit = (next: string) => {
    onChange(next)
    if (next.length === length) onComplete?.(next)
  }

  const handleChange = (index: number, raw: string) => {
    const incoming = raw.replace(/\D/g, '')
    if (!incoming) return

    // Fill forward from this box. Usually `incoming` is a single digit, but it
    // can be several: focus only moves after React re-renders, so a fast typist
    // (or an automated one) lands every keystroke in the SAME box before the
    // caret advances. Taking just one character there silently dropped all but
    // the last digit — typing "4620" left "0".
    const chars = incoming.slice(0, length - index).split('')
    const next = digits.slice()
    chars.forEach((c, offset) => {
      next[index + offset] = c
    })

    const joined = next.join('').slice(0, length)
    commit(joined)

    const nextFocus = Math.min(index + chars.length, length - 1)
    inputRefs.current[nextFocus]?.focus()
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Backspace') return
    if (digits[index]) {
      // Clear this box first; a second backspace steps back.
      commit(digits.map((d, i) => (i === index ? '' : d)).join(''))
      return
    }
    if (index > 0) {
      inputRefs.current[index - 1]?.focus()
      commit(digits.map((d, i) => (i === index - 1 ? '' : d)).join(''))
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    e.preventDefault()
    commit(pasted)
    inputRefs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <Stack direction='row' justifyContent='center'>
      {digits.map((digit, index) => (
        <Box
          key={index}
          sx={{
            width: '32px',
            height: '44px',
            margin: '0 4px',
            // Slightly wider gutter after the second box, matching sign-in.
            '&:nth-of-type(2)': { margin: '0 16px 0 4px' },
          }}
        >
          <TextField
            value={digit}
            disabled={disabled}
            inputRef={(el: HTMLInputElement | null) => {
              inputRefs.current[index] = el
            }}
            className={error ? 'error otpInput' : 'otpInput'}
            onChange={e => handleChange(index, e.target.value)}
            onKeyDown={e => handleKeyDown(index, e)}
            onPaste={handlePaste}
            placeholder='&mdash;'
            size='small'
            inputProps={{
              type: 'number',
              pattern: '[0-9]*',
              inputMode: 'numeric',
              'aria-label': `Digit ${index + 1} of ${length}`,
            }}
          />
        </Box>
      ))}
    </Stack>
  )
}
